import { useEffect, useMemo, useState } from "react";
import { apiPost } from "../client/apiClient.js";
import type { ApiResult, PriceBreakdown } from "../shared/types.js";
import { CheckIcon, ClockIcon } from "../components/Icons.js";
import { LoadingState } from "../components/States.js";

const BUILDING_OPTIONS: { value: string; label: string }[] = [
  { value: "appartement", label: "Appartement" },
  { value: "rijwoning", label: "Rijwoning" },
  { value: "halfopen_woning", label: "Halfopen woning" },
  { value: "open_bebouwing", label: "Open bebouwing" },
  { value: "ander_gebouw", label: "Ander gebouw" },
];

const FLOOR_OPTIONS = Array.from({ length: 6 }, (_, i) => i + 1).map((n) => ({
  value: String(n),
  label: `${n} ${n === 1 ? "bouwlaag" : "bouwlagen"}`,
}));

const YEAR_OPTIONS = [
  { value: "1975", label: "Voor 2001" },
  { value: "2010", label: "2001 of later" },
];

function formatEuro(cents: number): string {
  return (cents / 100).toLocaleString("nl-BE", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export function PriceEstimator({ fromPriceCents }: { fromPriceCents: number }) {
  const [buildingType, setBuildingType] = useState("appartement");
  const [floors, setFloors] = useState("1");
  const [hasBasement, setHasBasement] = useState(false);
  const [buildYear, setBuildYear] = useState("1975");
  const [result, setResult] = useState<PriceBreakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = useMemo(
    () => ({ buildingType, floors: Number(floors), hasBasement, surfaceM2: 100, buildYear: Number(buildYear) }),
    [buildingType, floors, hasBasement, buildYear]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const timer = setTimeout(async () => {
      try {
        const res = (await apiPost("/api/price/calculate", { building: params, service: {} })) as ApiResult<PriceBreakdown>;
        if (cancelled) return;
        if (res.ok) {
          setResult(res.data);
        } else {
          setError(res.error);
        }
      } catch {
        if (!cancelled) setError("Prijs kon niet berekend worden. Probeer het later opnieuw.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [params]);

  const nextHref = `/prijs-en-boeking?buildingType=${buildingType}&floors=${floors}&hasBasement=${hasBasement}&buildYear=${buildYear}`;
  const attestNotRequired = Boolean(result && !loading && !error && result.attestNotRequired);

  return (
    <div className="card card-pad" style={{ maxWidth: 900, margin: "0 auto" }}>
      <div className="grid grid-3">
        <div className="form-field mb-0">
          <label htmlFor="pe-building">Type gebouw</label>
          <select id="pe-building" className="form-select" value={buildingType} onChange={(e: any) => setBuildingType(e.target.value)}>
            {BUILDING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field mb-0">
          <label htmlFor="pe-floors">Aantal woonlagen</label>
          <select id="pe-floors" className="form-select" value={floors} onChange={(e: any) => setFloors(e.target.value)}>
            {FLOOR_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field mb-0">
          <label htmlFor="pe-year">Bouwjaar</label>
          <select id="pe-year" className="form-select" value={buildYear} onChange={(e: any) => setBuildYear(e.target.value)}>
            {YEAR_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-field mb-0 mt-3">
        <label>
          <input type="checkbox" checked={hasBasement} onChange={(e: any) => setHasBasement(e.target.checked)} /> Kelder
          aanwezig
        </label>
      </div>

      <div className="flex items-center justify-between mt-6 flex-wrap gap-4">
        <div>
          {loading && <LoadingState label="Prijs wordt berekend…" />}
          {!loading && error && <p className="validation-message">{error}</p>}
          {!loading && !error && result && result.attestNotRequired && (
            <p className="mb-0 text-small">
              <strong>Geen asbestattest nodig.</strong> {result.attestNotRequiredReason}
            </p>
          )}
          {!loading && !error && result && !result.requiresCustomQuote && !result.attestNotRequired && (
            <>
              <div className="text-tiny text-muted">Uw prijsindicatie</div>
              <div style={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: "2rem", color: "var(--color-orange)" }}>
                &euro;{formatEuro(result.totalInclVatCents)}{" "}
                <span className="text-small text-muted" style={{ fontWeight: 500 }}>
                  incl. btw
                </span>
              </div>
              <div className="trust-point mt-2">
                <ClockIcon size={16} /> Binnen {result.estimatedDeliveryDays} werkdagen
              </div>
            </>
          )}
          {!loading && !error && result?.requiresCustomQuote && (
            <p className="mb-0 text-small">{result.customQuoteReason}</p>
          )}
        </div>
        {!attestNotRequired && (
          <a href={nextHref} className="btn btn-primary btn-lg" data-event="start_price_calc">
            Volgende stap
          </a>
        )}
      </div>
      <p className="text-tiny text-muted mt-4 mb-0">
        <CheckIcon size={12} /> Indicatieve prijs op basis van uw invoer. In stap 2 van de boeking verfijnt u de
        details en ziet u de volledige uitsplitsing.
      </p>
    </div>
  );
}
